import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ChatCircleDots, PaperPlaneTilt, X, Sparkle, Lock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const SEED_PROMPTS = [
  'How do I use this app? What should I do first?',
  'What should I study next based on my progress?',
  'Explain FIFO vs LIFO with a worked example.',
  'Walk me through an adjusting entry for accrued wages.'
];

export function AITutor({ isPro, onUpgrade, open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const sendMessage = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setError('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/tutor/chat`, { session_id: sessionId, message: msg });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      if (err.response?.status === 402) {
        setError('Pro subscription required to chat with the AI Tutor.');
      } else {
        setError(err.response?.data?.detail || 'Tutor unavailable. Try again.');
      }
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        data-testid="tutor-overlay"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full md:max-w-2xl h-[90vh] md:h-[80vh] flex flex-col rounded-t-2xl md:rounded-lg overflow-hidden"
          style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          data-testid="tutor-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--primary)' }}>
            <div className="flex items-center gap-3 text-white">
              <Sparkle size={22} weight="fill" />
              <div>
                <div className="font-display font-bold">AI Accounting Tutor</div>
                <div className="text-xs opacity-80 font-mono">Master Accountant · Claude 4.5</div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:opacity-70"
              data-testid="tutor-close-btn"
            >
              <X size={22} weight="bold" />
            </button>
          </div>

          {/* Body */}
          {!isPro ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                <Lock size={28} weight="fill" style={{ color: 'var(--primary)' }} />
              </div>
              <h3 className="font-display text-2xl font-black" style={{ color: 'var(--text-primary)' }}>
                Unlock the AI Tutor
              </h3>
              <p className="text-sm max-w-sm opacity-70">
                Ask anything — FIFO vs LIFO, adjusting entries, ratio analysis — and get step-by-step
                answers with formulas and worked examples. Included in Pro.
              </p>
              <button
                onClick={onUpgrade}
                className="btn-primary px-6 py-3"
                data-testid="tutor-upgrade-btn"
              >
                Upgrade to Pro — $9.99
              </button>
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="tutor-messages">
                {messages.length === 0 && (
                  <div className="space-y-4">
                    <div className="font-mono text-xs uppercase tracking-widest opacity-60">Try asking</div>
                    <div className="grid gap-2">
                      {SEED_PROMPTS.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => sendMessage(p)}
                          className="text-left text-sm p-3 rounded border transition-all hover:bg-[#F0EBE1]"
                          style={{ borderColor: 'var(--border)' }}
                          data-testid={`tutor-seed-${i}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] p-3 rounded-lg text-sm whitespace-pre-wrap ${
                        m.role === 'user' ? 'bg-[#163300] text-white' : 'bg-[#F0EBE1]'
                      }`}
                      style={{ color: m.role === 'user' ? 'white' : 'var(--text-primary)' }}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%] p-3 rounded-lg text-sm bg-[#F0EBE1] flex gap-2 items-center">
                      <motion.div
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                      >
                        The Master is thinking…
                      </motion.div>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="p-3 rounded text-sm" style={{ backgroundColor: '#8B000010', color: '#8B0000', border: '1px solid #8B0000' }}>
                    {error}
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendMessage();
                  }}
                  className="flex gap-2"
                >
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask about accounting concepts, formulas, journal entries…"
                    className="flex-1 px-4 py-3 rounded border font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#163300]"
                    style={{ borderColor: 'var(--border)' }}
                    disabled={sending}
                    data-testid="tutor-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="btn-primary px-4 py-3 flex items-center gap-2 disabled:opacity-50"
                    data-testid="tutor-send-btn"
                  >
                    <PaperPlaneTilt size={18} weight="fill" />
                  </button>
                </form>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function TutorFab({ onOpen }) {
  return (
    <motion.button
      drag
      dragMomentum={false}
      dragElastic={0}
      onClick={onOpen}
      whileTap={{ scale: 0.95 }}
      className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing"
      style={{ backgroundColor: 'var(--primary)', color: 'white' }}
      title="AI Accounting Tutor (drag to move)"
      data-testid="tutor-fab"
    >
      <ChatCircleDots size={26} weight="fill" />
    </motion.button>
  );
}
